/**
 * useNewsFeed — server state + filter state for the Department News feed.
 *
 * Reads via useQuery (DEVELOPMENT_RULES rule 3); the only write is the
 * on-demand refresh, a useMutation that settles into an invalidate.
 *
 * WHO the feed is about comes from the global header, exactly like every
 * other data page (the GAS FilterContext pattern): a selected agency wins,
 * otherwise the active account list, otherwise every agency. The page only
 * owns what the header cannot express — the lane (agencies / statewide /
 * industry), categories, search and the recency window — and those
 * round-trip through the URL so /news?lane=state deep-links.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';

export const SINCE_OPTIONS = [
  ['7d', '7 days'], ['30d', '30 days'], ['90d', '90 days'], ['365d', '1 year'],
];

/** Lane pills. 'agencies' follows the header; the other two are stored-lane views. */
export const NEWS_LANE_OPTIONS = [
  ['agencies', 'Agencies', 'Department news, scoped by the agency and account list in the header'],
  ['state', 'Statewide', 'Capitol, budget and statewide IT with no single department'],
  ['industry', 'Industry', 'Cybersecurity and technology beyond California — research, incidents, vendors'],
];
const LANES = NEWS_LANE_OPTIONS.map(([k]) => k);

function parseInitial(search) {
  const p = new URLSearchParams(search || '');
  // Legacy links used scope=state|industry; mine/all both mean "agencies" now.
  const lane = p.get('lane') || p.get('scope');
  return {
    lane: LANES.includes(lane) ? lane : 'agencies',
    categories: (p.get('categories') || '').split(',').filter(Boolean),
    q: p.get('q') || '',
    since: p.get('since') || '30d',
  };
}

export function filtersToSearch(f) {
  const p = new URLSearchParams();
  if (f.lane && f.lane !== 'agencies') p.set('lane', f.lane);
  if (f.categories?.length) p.set('categories', f.categories.join(','));
  if (f.q) p.set('q', f.q);
  if (f.since && f.since !== '30d') p.set('since', f.since);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * @param {string} initialSearch  location.search on first render
 * @param {{ departmentId?: number|null, listDepartmentIds?: number[] }} headerScope
 *   From the header: the selected agency (overrides the list — the locked
 *   ListsContext decision) and the active list's resolved department ids.
 */
export default function useNewsFeed(initialSearch = '', headerScope = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(() => parseInitial(initialSearch));
  const [pages, setPages] = useState([]); // older pages appended via next_before
  const [notice, setNotice] = useState(null);

  const set = useCallback((patch) => {
    setPages([]);
    setFilters((f) => ({ ...f, ...(typeof patch === 'function' ? patch(f) : patch) }));
  }, []);

  const toggleCategory = useCallback((c) => set((f) => ({
    categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
  })), [set]);

  // Header → backend vocabulary. departments (ids) overrides scope=mine's
  // server-side set, so scope is only ever 'all' or a stored lane here.
  const departmentIds = useMemo(() => {
    if (filters.lane !== 'agencies') return [];
    if (headerScope.departmentId != null) return [headerScope.departmentId];
    return headerScope.listDepartmentIds || [];
  }, [filters.lane, headerScope.departmentId, headerScope.listDepartmentIds]);

  const query = useMemo(() => {
    const q = { scope: filters.lane === 'agencies' ? 'all' : filters.lane, since: filters.since, limit: 50 };
    if (departmentIds.length) q.departments = departmentIds.join(',');
    if (filters.categories.length) q.categories = filters.categories.join(',');
    if (filters.q) q.q = filters.q;
    return q;
  }, [filters, departmentIds]);

  const feedQuery = useQuery({
    queryKey: ['department-news', query],
    queryFn: () => apiClient.get('/api/v1/department-news', { query }),
    staleTime: 60_000,
    keepPreviousData: true,
  });

  const loadMore = useCallback(async () => {
    const last = pages.length ? pages[pages.length - 1] : feedQuery.data;
    if (!last?.next_before) return;
    try {
      const older = await apiClient.get('/api/v1/department-news', { query: { ...query, before: last.next_before } });
      setPages((p) => [...p, older]);
    } catch (e) {
      setNotice({ kind: 'err', text: e?.message || 'Could not load older items.' });
    }
  }, [pages, feedQuery.data, query]);

  const refresh = useMutation({
    mutationFn: (account) => apiClient.post(`/api/v1/department-news/refresh/${encodeURIComponent(account)}`, {}),
    onMutate: () => setNotice(null),
    onError: (e) => setNotice({ kind: 'err', text: e?.message || 'Refresh failed.' }),
    onSuccess: (r) => {
      const added = (r?.gnews?.added || 0) + (r?.rss?.added || 0);
      setNotice({ kind: 'ok', text: added ? `Pulled ${added} new item(s).` : 'Up to date — nothing new upstream.' });
      queryClient.invalidateQueries({ queryKey: ['department-news'] });
    },
  });

  const items = useMemo(() => {
    const seen = new Set();
    const all = [...(feedQuery.data?.items || []), ...pages.flatMap((p) => p.items || [])];
    return all.filter((it) => (seen.has(it.id) ? false : (seen.add(it.id), true)));
  }, [feedQuery.data, pages]);

  const last = pages.length ? pages[pages.length - 1] : feedQuery.data;

  return {
    filters, set, toggleCategory, departmentIds,
    items, counts: feedQuery.data?.counts || { total_window: 0, by_category: {} },
    emptyReason: feedQuery.data?.empty_reason || null,
    isLoading: feedQuery.isLoading, isFetching: feedQuery.isFetching, error: feedQuery.error,
    hasMore: !!last?.next_before, loadMore,
    refresh, notice, setNotice,
    search: filtersToSearch(filters),
  };
}
